import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Search,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Ban,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  Eye,
  Timer,
  Car,
  CreditCard,
  Shield,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  consumed: { label: 'Consumed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  revoked: { label: 'Revoked', color: 'bg-red-100 text-red-800', icon: Ban },
};

const sourceConfig = {
  payment: { label: 'Payment', icon: CreditCard },
  entitlement: { label: 'Entitlement', icon: Shield },
  freeflow: { label: 'Freeflow', icon: Car },
  manual: { label: 'Manual', icon: Plus },
  permit: { label: 'Permit', icon: Shield },
  waiver: { label: 'Waiver', icon: Minus },
};

function formatRemainingTime(seconds) {
  if (!seconds || seconds <= 0) return 'Expired';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
  }
  return `${mins}m ${secs}s`;
}

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminExitAuths() {
  const navigate = useNavigate();
  const [authorisations, setAuthorisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  
  // Filters
  const [searchVRM, setSearchVRM] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterSource, setFilterSource] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Modals
  const [selectedAuth, setSelectedAuth] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [extendMinutes, setExtendMinutes] = useState(15);
  const [extendReason, setExtendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Auto-refresh for active countdown
  const [refreshCounter, setRefreshCounter] = useState(0);

  const fetchAuthorisations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('skip', page * limit);
      
      if (searchVRM) params.append('vehicle_registration', searchVRM);
      if (filterStatus) params.append('status', filterStatus);
      if (filterSite) params.append('site_id', filterSite);
      if (filterSource) params.append('source', filterSource);
      
      const response = await fetch(`${API_URL}/api/exit-auth?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to fetch authorisations');
      
      const data = await response.json();
      setAuthorisations(data.authorisations || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching authorisations:', error);
      toast.error('Failed to load exit authorisations');
    } finally {
      setLoading(false);
    }
  }, [page, searchVRM, filterStatus, filterSite, filterSource]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filterSite ? `?site_id=${filterSite}` : '';
      const response = await fetch(`${API_URL}/api/exit-auth/stats${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data.sites || data || []);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  useEffect(() => {
    fetchAuthorisations();
    fetchStats();
  }, [fetchAuthorisations, refreshCounter]);

  useEffect(() => {
    fetchSites();
  }, []);

  // Auto-refresh countdown every 10 seconds for active authorisations
  useEffect(() => {
    const hasActive = authorisations.some(a => a.status === 'active');
    if (hasActive) {
      const interval = setInterval(() => {
        setRefreshCounter(c => c + 1);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [authorisations]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchAuthorisations();
  };

  const handleRevoke = async () => {
    if (!selectedAuth || !revokeReason.trim()) {
      toast.error('Please provide a reason for revocation');
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/exit-auth/${selectedAuth.exit_auth_id}/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: revokeReason }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to revoke');
      }
      
      toast.success('Exit authorisation revoked');
      setShowRevokeModal(false);
      setRevokeReason('');
      setSelectedAuth(null);
      fetchAuthorisations();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!selectedAuth || extendMinutes < 1) {
      toast.error('Please specify valid extension time');
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/exit-auth/${selectedAuth.exit_auth_id}/extend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          additional_minutes: extendMinutes,
          reason: extendReason || null,
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to extend');
      }
      
      const result = await response.json();
      toast.success(`Extended by ${extendMinutes} minutes`);
      setShowExtendModal(false);
      setExtendMinutes(15);
      setExtendReason('');
      setSelectedAuth(null);
      fetchAuthorisations();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const viewDetails = async (auth) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/exit-auth/${auth.exit_auth_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedAuth(data);
        setShowDetailModal(true);
      } else {
        setSelectedAuth(auth);
        setShowDetailModal(true);
      }
    } catch (error) {
      setSelectedAuth(auth);
      setShowDetailModal(true);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`} data-testid={`status-badge-${status}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const SourceBadge = ({ source }) => {
    const config = sourceConfig[source] || { label: source, icon: Shield };
    const Icon = config.icon;
    return (
      <Badge variant="outline" className="flex items-center gap-1" data-testid={`source-badge-${source}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-exit-auths-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exit Authorisations</h1>
          <p className="text-gray-500">Manage barrier exit permissions</p>
        </div>
        <Button
          variant="outline"
          onClick={() => { fetchAuthorisations(); fetchStats(); }}
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:border-green-400 transition-colors"
            onClick={() => setFilterStatus('active')}
            data-testid="stats-active"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Currently Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.currently_active}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => setFilterStatus('consumed')}
            data-testid="stats-consumed"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Consumed Today</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.today?.consumed || 0}</p>
                </div>
                <Car className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-gray-400 transition-colors"
            onClick={() => setFilterStatus('expired')}
            data-testid="stats-expired"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Expired Today</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.today?.expired || 0}</p>
                </div>
                <XCircle className="h-8 w-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-red-400 transition-colors"
            onClick={() => setFilterStatus('revoked')}
            data-testid="stats-revoked"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Revoked Today</p>
                  <p className="text-2xl font-bold text-red-600">{stats.today?.revoked || 0}</p>
                </div>
                <Ban className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search VRM</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Enter registration..."
                  value={searchVRM}
                  onChange={(e) => setSearchVRM(e.target.value.toUpperCase())}
                  className="pl-10"
                  data-testid="search-vrm-input"
                />
              </div>
            </div>
            
            <div className="w-[150px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={filterStatus} onValueChange={(val) => { setFilterStatus(val === 'all' ? '' : val); setPage(0); }}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="consumed">Consumed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Site</label>
              <Select value={filterSite} onValueChange={(val) => { setFilterSite(val === 'all' ? '' : val); setPage(0); }}>
                <SelectTrigger data-testid="filter-site">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((site) => (
                    <SelectItem key={site.site_id} value={site.site_id}>
                      {site.name || site.site_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[150px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Source</label>
              <Select value={filterSource} onValueChange={(val) => { setFilterSource(val === 'all' ? '' : val); setPage(0); }}>
                <SelectTrigger data-testid="filter-source">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="entitlement">Entitlement</SelectItem>
                  <SelectItem value="freeflow">Freeflow</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button type="submit" data-testid="search-btn">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : authorisations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Car className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No exit authorisations found</p>
            </div>
          ) : (
            <>
              <Table data-testid="exit-auths-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>VRM</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Remaining</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Payment Ref</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {authorisations.map((auth) => (
                    <TableRow key={auth.exit_auth_id} data-testid={`auth-row-${auth.exit_auth_id}`}>
                      <TableCell className="font-mono font-bold">
                        {auth.vehicle_registration}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={auth.status} />
                      </TableCell>
                      <TableCell>
                        <SourceBadge source={auth.source} />
                      </TableCell>
                      <TableCell>
                        {auth.status === 'active' ? (
                          <span className={`flex items-center gap-1 ${auth.remaining_seconds < 300 ? 'text-red-600 font-bold' : 'text-green-600'}`}>
                            <Timer className="h-4 w-4" />
                            {formatRemainingTime(auth.remaining_seconds)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDateTime(auth.created_at)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDateTime(auth.expires_at)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {auth.payment_id || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewDetails(auth)}
                            data-testid={`view-btn-${auth.exit_auth_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          
                          {auth.status === 'active' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedAuth(auth);
                                  setShowExtendModal(true);
                                }}
                                data-testid={`extend-btn-${auth.exit_auth_id}`}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Extend
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setSelectedAuth(auth);
                                  setShowRevokeModal(true);
                                }}
                                data-testid={`revoke-btn-${auth.exit_auth_id}`}
                              >
                                <Ban className="h-4 w-4 mr-1" />
                                Revoke
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * limit >= total}
                    onClick={() => setPage(p => p + 1)}
                    data-testid="next-page-btn"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Exit Authorisation Details</DialogTitle>
            <DialogDescription>
              {selectedAuth?.exit_auth_id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAuth && (
            <div className="space-y-4" data-testid="detail-modal-content">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Vehicle Registration</p>
                  <p className="font-mono font-bold text-lg">{selectedAuth.vehicle_registration}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <StatusBadge status={selectedAuth.status} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source</p>
                  <SourceBadge source={selectedAuth.source} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Grace Period</p>
                  <p>{selectedAuth.grace_period_minutes} minutes</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p>{formatDateTime(selectedAuth.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expires</p>
                  <p>{formatDateTime(selectedAuth.expires_at)}</p>
                </div>
                {selectedAuth.status === 'active' && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Remaining Time</p>
                    <p className={`text-2xl font-bold ${selectedAuth.remaining_seconds < 300 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatRemainingTime(selectedAuth.remaining_seconds)}
                    </p>
                  </div>
                )}
                {selectedAuth.parking_session_id && (
                  <div>
                    <p className="text-sm text-gray-500">Session ID</p>
                    <p className="font-mono text-sm">{selectedAuth.parking_session_id}</p>
                  </div>
                )}
                {selectedAuth.payment_id && (
                  <div>
                    <p className="text-sm text-gray-500">Payment Reference</p>
                    <p className="font-mono text-sm">{selectedAuth.payment_id}</p>
                  </div>
                )}
                {selectedAuth.barrier_opened && (
                  <div>
                    <p className="text-sm text-gray-500">Barrier Opened At</p>
                    <p>{formatDateTime(selectedAuth.barrier_opened_at)}</p>
                  </div>
                )}
                {selectedAuth.revoked_at && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500">Revoked At</p>
                      <p>{formatDateTime(selectedAuth.revoked_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Revoke Reason</p>
                      <p className="text-red-600">{selectedAuth.revoked_reason}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Audit History */}
              {selectedAuth.audit_history && selectedAuth.audit_history.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Audit History</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                    {selectedAuth.audit_history.map((entry, idx) => (
                      <div key={idx} className="text-sm border-b pb-2 last:border-b-0">
                        <div className="flex justify-between">
                          <span className="font-medium capitalize">{entry.action.replace(/_/g, ' ')}</span>
                          <span className="text-gray-500">{formatDateTime(entry.timestamp)}</span>
                        </div>
                        <p className="text-gray-500">By: {entry.by}</p>
                        {entry.details && (
                          <pre className="text-xs bg-gray-50 p-1 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(entry.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Barrier Reads */}
              {selectedAuth.barrier_reads && selectedAuth.barrier_reads.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Barrier Reads</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto border rounded-lg p-2">
                    {selectedAuth.barrier_reads.map((read, idx) => (
                      <div key={idx} className="text-sm flex justify-between">
                        <span>{read.action} - {read.result}</span>
                        <span className="text-gray-500">{formatDateTime(read.read_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Modal */}
      <Dialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Ban className="h-5 w-5" />
              Revoke Exit Authorisation
            </DialogTitle>
            <DialogDescription>
              This will prevent the vehicle from exiting through the barrier.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAuth && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  You are about to revoke the exit authorisation for:
                </p>
                <p className="font-mono font-bold text-lg text-red-900 mt-1">
                  {selectedAuth.vehicle_registration}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Reason for Revocation *
                </label>
                <Input
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Enter reason..."
                  data-testid="revoke-reason-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRevokeModal(false);
                setRevokeReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={actionLoading || !revokeReason.trim()}
              data-testid="confirm-revoke-btn"
            >
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
              Revoke Authorisation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Modal */}
      <Dialog open={showExtendModal} onOpenChange={setShowExtendModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Extend Grace Period
            </DialogTitle>
            <DialogDescription>
              Add additional time for the vehicle to exit.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAuth && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  Extending exit authorisation for:
                </p>
                <p className="font-mono font-bold text-lg text-green-900 mt-1">
                  {selectedAuth.vehicle_registration}
                </p>
                <p className="text-sm text-green-700 mt-2">
                  Current expiry: {formatDateTime(selectedAuth.expires_at)}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Additional Minutes *
                </label>
                <Select
                  value={String(extendMinutes)}
                  onValueChange={(val) => setExtendMinutes(Number(val))}
                >
                  <SelectTrigger data-testid="extend-minutes-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="10">10 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Reason (Optional)
                </label>
                <Input
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  placeholder="Enter reason..."
                  data-testid="extend-reason-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowExtendModal(false);
                setExtendMinutes(15);
                setExtendReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExtend}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-extend-btn"
            >
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Extend by {extendMinutes} minutes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
